import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { stripeAPI } from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';

const MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID || 'price_1RClBZFVY7WJO8Cc1xXQZ0Gv';

export default function Subscription() {
  const { t } = useTranslation();
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activating, setActivating] = useState(false);

  const isActive = user?.subscriptionStatus === 'active';

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await stripeAPI.createCheckout(MONTHLY_PRICE_ID) as { success?: boolean; url?: string; message?: string };
      if (result?.success && result?.url) {
        window.location.href = result.url;
      } else {
        setError(result?.message || t('subscription.checkoutError'));
      }
    } catch (err: any) {
      setError(err.message || t('subscription.checkoutError'));
    } finally {
      setLoading(false);
    }
  };

  const handleManage = async () => {
    try {
      const result = await stripeAPI.getPortal() as { success?: boolean; url?: string };
      if (result?.success && result?.url) {
        window.location.href = result.url;
      }
    } catch (err: any) {
      setError(err.message || t('subscription.portalError'));
    }
  };

  // Handle Stripe redirect back
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const subSuccess = searchParams.get('subscription') === 'success' || hashParams.get('subscription') === 'success';
    const subCancelled = searchParams.get('cancelled') === 'true' || hashParams.get('cancelled') === 'true';

    if (subSuccess && !isActive) {
      setActivating(true);
      const poll = async (attempts = 0) => {
        await refreshUser();
        if (attempts >= 5) {
          navigate('/');
        } else {
          setTimeout(() => poll(attempts + 1), 2000);
        }
      };
      poll();
    }

    if (subCancelled) {
      setError(t('subscription.cancelled'));
    }
  }, []);

  // Navigate away once subscription becomes active
  useEffect(() => {
    if (isActive && activating) {
      navigate('/');
    }
  }, [isActive, activating]);

  if (isActive && !activating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('subscription.active')}</h2>
          <p className="text-gray-600 mb-1">{t('subscription.plan')}: <span className="font-semibold capitalize">{user?.subscriptionPlan || 'monthly'}</span></p>
          <div className="flex gap-3 mt-6 justify-center">
            <button onClick={() => navigate('/')} className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{t('subscription.goToDashboard')}</button>
            <button onClick={handleManage} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">{t('subscription.manage')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Muscle Guide</h1>
          <p className="text-lg text-gray-600">{t('subscription.headline')}</p>
        </div>

        {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-center mb-6">{error}</div>}
        {activating && (
          <div className="bg-blue-50 text-blue-700 p-3 rounded-lg text-center mb-6 flex items-center justify-center gap-2">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            {t('subscription.activating')}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-indigo-200">
          <h3 className="text-xl font-bold text-gray-900 mb-1">{t('subscription.monthlyPlan')}</h3>
          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-4xl font-bold text-indigo-600">$10</span>
            <span className="text-gray-500">/{t('subscription.month')}</span>
          </div>
          <ul className="space-y-2 mb-6 text-sm text-gray-600">
            {['aiWorkoutPlans', 'aiDietPlans', 'aiChatAssistant', 'progressTracking', 'evolutionCharts'].map(feat => (
              <li key={feat} className="flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                {t(`subscription.features.${feat}`)}
              </li>
            ))}
          </ul>
          <button
            onClick={handleSubscribe}
            disabled={loading || activating}
            className="w-full py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {loading ? t('subscription.redirecting') : t('subscription.subscribeBtn')}
          </button>
        </div>

        <div className="text-center mt-8">
          <button onClick={logout} className="text-sm text-gray-500 hover:text-gray-700">
            {t('auth.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}
